/* Action sheet for a booked square: stop, reopen, correct its clock, change
 * service, mark as a client request, remove.
 *
 * Not what a square on the grid opens any more. Clicking one — either button —
 * gets js/components/turn-menu.js, a card anchored under the pointer holding
 * the same list, because a dialog in the middle of the window made the desk
 * look away from the column they were working to read facts back about it.
 *
 * This is still here for the two ways in that have no pointer to anchor to:
 * the appointments pane, where the thing clicked is a booking rather than a
 * square, and the phone's list, which has no right-click and not much room for
 * a card either. Both open it by turn id; neither has a position to pass. */
import * as Vue from 'vue';
import { store } from '../store.js';

export const CellMenu = {
  name: 'CellMenu',
  setup: function () {
    var confirmRemove = Vue.ref(false);
    var changing = Vue.ref(false);
    var editingTimes = Vue.ref(false);
    var startInput = Vue.ref('');
    var endInput = Vue.ref('');
    var turn = store.cellMenuTurn;

    // Seeded from the square each time, so cancelling and reopening the
    // editor never shows a stale value somebody half-typed earlier.
    function editTimes() {
      if (!turn.value) return;
      startInput.value = store.clockValue(turn.value.startedAt);
      endInput.value = store.clockValue(turn.value.endedAt);
      editingTimes.value = true;
    }

    function saveTimes() {
      if (!turn.value) return;
      if (store.setTurnTimes(turn.value.id, startInput.value, endInput.value)) {
        editingTimes.value = false;
      }
    }

    var staff = Vue.computed(function () {
      if (!turn.value) return null;
      return store.staffOf(turn.value.staffId);
    });

    // Turn number is the square's position, so paired halves share one number
    // and any missed or skipped squares above it are counted.
    var turnNumber = Vue.computed(function () {
      return turn.value ? store.turnRowNumber(turn.value.id) : 0;
    });

    // The other half sharing this square, so both are reachable from here.
    var mates = Vue.computed(function () {
      if (!turn.value) return [];
      var list = store.slotMatesOf(turn.value.id);
      return list.length > 1 ? list : [];
    });

    // Everything on the booking, not only the options carrying a rule: the
    // square's + counts them all, and a sheet that listed fewer than the mark
    // promised would read as a bug.
    var options = Vue.computed(function () {
      return turn.value ? store.optionsOfTurn(turn.value) : [];
    });

    // Which of them is the reason this square is the type it is. The rest are
    // notes on the booking, and the sheet says which is which rather than
    // leaving the desk to guess why a pedicure counts as a full turn.
    function optionRule(o) {
      var r = store.optionRuleFor(o.id);
      if (!r || !r.serviceId) return null;
      return store.serviceById.value[r.serviceId] || null;
    }

    // The three weights read as three different sentences — "worth no turn"
    // is not English, and a zero-turn service is worth saying out loud.
    var WORTH = {
      0: ', which does not count as a turn at all',
      0.5: ', worth half a turn',
      1: ', worth a full turn'
    };

    function optionTitle(o) {
      var svc = optionRule(o);
      if (!svc) return o.name + ' — a note on the booking; it does not change the turn';
      var article = /^[aeiou]/i.test(svc.name) ? 'an ' : 'a ';
      return o.name + ' — makes this ' + article + svc.name +
        (WORTH[svc.turnWeight] || '');
    }

    /* Every service on this square, when there is more than one.
     *
     * The whole reason a merged square exists is that the desk said these
     * happened in one sitting, and the only way to check that later is to see
     * them side by side with their own clocks. Empty for the ordinary square,
     * which is most of them — a section headed "Services" over a list of one is
     * noise on a sheet that already names the service in its heading. */
    var merged = Vue.computed(function () {
      if (!turn.value || !store.isMergedTurn(turn.value)) return [];
      return store.servicesOfTurn(turn.value);
    });

    // What the combined square may be set to. The same choices the merge sheet
    // offered, asked again from here because a decision made in a second at the
    // counter is read for the rest of the day.
    var weights = Vue.computed(function () {
      if (!merged.value.length) return [];
      var own = merged.value.map(function (s) { return s.weight || 0; });
      return store.mergeWeightChoices(own[0], own.slice(1).reduce(function (a, b) {
        return a + b;
      }, 0));
    });

    var elapsed = Vue.computed(function () {
      if (!turn.value) return '0:00';
      var end = turn.value.endedAt == null ? store.now.value : turn.value.endedAt;
      return store.formatDuration(end - turn.value.startedAt);
    });

    // The square's colour, from the store — the same three lines lived in
    // this file, move-confirm and attach-appointment.
    var color = Vue.computed(function () {
      return store.colorOfTurn(turn.value);
    });

    Vue.watch(turn, function () {
      confirmRemove.value = false;
      changing.value = false;
      editingTimes.value = false;
    });

    // Changing a turn's service must not put a tech on something they don't
    // do. The service it already is stays enabled whatever their list says —
    // it can be booked off-menu deliberately, and greying out the current
    // selection would just look broken.
    function canDo(svc) {
      if (!staff.value) return true;
      if (turn.value && svc.id === turn.value.serviceId) return true;
      return staff.value.serviceIds.indexOf(svc.id) !== -1;
    }

    var offMenuCount = Vue.computed(function () {
      return store.activeServices.value.filter(function (s) { return !canDo(s); }).length;
    });

    return {
      turn: turn,
      staff: staff,
      turnNumber: turnNumber,
      mates: mates,
      openCellMenu: store.openCellMenu,
      serviceById: store.serviceById,
      colorOfTurn: store.colorOfTurn,
      elapsed: elapsed,
      color: color,
      options: options,
      optionRule: optionRule,
      optionTitle: optionTitle,
      merged: merged,
      weights: weights,
      setMergedWeight: store.setMergedWeight,
      unmergeFromTurn: store.unmergeFromTurn,
      confirmRemove: confirmRemove,
      changing: changing,
      editingTimes: editingTimes,
      startInput: startInput,
      endInput: endInput,
      editTimes: editTimes,
      saveTimes: saveTimes,
      canDo: canDo,
      weightLabel: store.weightLabel,
      offMenuCount: offMenuCount,
      services: store.activeServices,
      formatClock: store.formatClock,
      formatCredit: store.formatCredit,
      close: store.closeCellMenu,
      stopTurn: store.stopTurn,
      startWaitingNow: store.startWaitingNow,
      reopenTurn: store.reopenTurn,
      setTurnService: store.setTurnService,
      toggleTurnRequested: store.toggleTurnRequested,
      ui: store.ui,
      removeTurn: store.removeTurn
    };
  },
  template: [
    '<div v-if="turn" class="modal-scrim" @click.self="close">',
    '  <div class="sheet" role="dialog" aria-modal="true">',
    '    <div class="sheet__head" :style="{ \'--svc\': color }">',
    '      <span class="sheet__letter">{{ turn.serviceLetter }}</span>',
    '      <div>',
    '        <h3>{{ turn.serviceName }}</h3>',
    '        <p>{{ staff ? staff.name : turn.staffName }} &middot; Turn {{ turnNumber }}</p>',
    '      </div>',
    '      <span v-if="ui.requestsOn && turn.requested" class="sheet__tag">&#9733; Request</span>',
    '    </div>',
    /* Directly under the head, because they are part of what the service in it
       IS. The one that set the type is filled in; the rest are notes. */
    '    <div v-if="options.length" class="sheet__opts">',
    '      <span class="sheet__opts-label">{{ options.length === 1 ? "Option" : "Options" }}</span>',
    '      <div class="sheet__opts-row">',
    '        <span v-for="o in options" :key="o.id" class="opt-pill"',
    '          :class="{ \'opt-pill--rules\': optionRule(o) }" :title="optionTitle(o)">',
    '          {{ o.name }}</span>',
    '      </div>',
    '    </div>',
    /* Everything this one square records. Above the facts, because "Counts as"
       down there is a number about this list and reads as a claim about the
       service in the heading until the list has been seen. Each with its own
       clock: whether these really were one sitting is a question about times,
       and the sheet is where somebody comes to check. */
    '    <div v-if="merged.length" class="sheet__merged">',
    '      <span class="sheet__merged-label">Services on this square</span>',
    '      <ul class="sheet__merged-list">',
    '        <li v-for="(s, i) in merged" :key="s.appointmentServiceId || i">',
    '          <span class="sheet__merged-dot" :style="{ background: s.color }"></span>',
    '          <span class="sheet__merged-name">{{ s.bookedName || s.name }}</span>',
    '          <span class="sheet__merged-time">{{ formatClock(s.startedAt) }} &ndash;',
    '            {{ s.endedAt ? formatClock(s.endedAt) : "now" }}</span>',
    /* Only the merged ones come off. The first is what the square IS — taking
       that away is removing the turn, which is the button at the bottom. */
    '          <button v-if="s.merged" type="button" class="sheet__merged-x"',
    '            :title="\'Take \' + (s.bookedName || s.name) + \' back off this square\'"',
    '            :aria-label="\'Take \' + (s.bookedName || s.name) + \' back off this square\'"',
    '            @click="unmergeFromTurn(turn.id, s.appointmentServiceId)">&times;</button>',
    '        </li>',
    '      </ul>',
    '    </div>',
    '    <div v-if="mates.length" class="sheet__mates">',
    '      <span class="sheet__mates-label">Half turns sharing this square</span>',
    '      <div class="sheet__mates-row">',
    '        <button v-for="m in mates" :key="m.id" type="button" class="chip chip--sm"',
    '          :class="{ \'chip--on\': m.id === turn.id }"',
    '          :style="{ \'--chip\': colorOfTurn(m) }"',
    '          @click="openCellMenu(m.id)">',
    '          <span class="chip__letter">{{ m.serviceLetter }}</span>',
    '          <span class="chip__name">{{ m.serviceName }}</span>',
    '        </button>',
    '      </div>',
    '    </div>',
    '    <dl class="sheet__facts">',
    '      <div><dt>Started</dt>',
    '        <dd v-if="!editingTimes">{{ formatClock(turn.startedAt) }}</dd>',
    '        <dd v-else><input class="input input--time" type="time" v-model="startInput" /></dd></div>',
    '      <div><dt>Ended</dt>',
    '        <dd v-if="!editingTimes">{{ turn.endedAt ? formatClock(turn.endedAt) : \'In progress\' }}</dd>',
    '        <dd v-else><input class="input input--time" type="time" v-model="endInput" /></dd></div>',
    '      <div><dt>Elapsed</dt><dd class="sheet__elapsed">{{ elapsed }}</dd></div>',
    /* On an ordinary square this is the service type's answer and there is
       nothing to decide. On a merged one it is the desk's — the one weight on
       the board no type can reconstruct — so it is the one place it can be
       changed after the merge sheet has been and gone. */
    '      <div><dt>Counts as</dt>',
    '        <dd v-if="!merged.length">{{ formatCredit(turn.turnWeight) }}</dd>',
    '        <dd v-else class="sheet__weight" role="radiogroup"',
    '          aria-label="What this square counts for">',
    '          <button v-for="w in weights" :key="w" type="button"',
    '            class="choice__weight-opt" :class="{ \'is-on\': turn.turnWeight === w }"',
    '            role="radio" :aria-checked="turn.turnWeight === w"',
    '            @click="setMergedWeight(turn.id, w)">{{ formatCredit(w) }}</button>',
    '        </dd></div>',
    '    </dl>',
    '    <div v-if="changing" class="sheet__services">',
    '      <button v-for="svc in services" :key="svc.id" type="button" class="chip chip--sm"',
    '        :class="{ \'chip--on\': svc.id === turn.serviceId }" :style="{ \'--chip\': svc.color }"',
    '        :disabled="!canDo(svc)"',
    '        :title="canDo(svc) ? svc.name : (staff ? staff.name + \' is not set up for \' + svc.name : svc.name)"',
    '        @click="setTurnService(turn.id, svc.id); changing = false">',
    '        <span class="chip__letter">{{ svc.abbrev }}</span>',
    '        <span class="chip__name">{{ svc.name }}</span>',
    '      </button>',
    '    </div>',
    '    <p v-if="changing && offMenuCount" class="sheet__services-note">',
    '      {{ offMenuCount }} greyed out &mdash; not on',
    '      {{ staff ? staff.name + "&rsquo;s" : "their" }} service list.',
    '      Add {{ offMenuCount === 1 ? "it" : "them" }} under <strong>Settings &rarr; Staff</strong>.</p>',
    '    <div class="sheet__actions">',
    /* Held until the client is checked in (see the store): start it by hand
       when Mangomint is not going to be told. */
    '      <button v-if="turn.waiting && turn.endedAt == null" type="button" class="btn btn--primary btn--lg"',
    '        @click="startWaitingNow(turn.id)">Start now &mdash; client is in the chair</button>',
    '      <p v-if="turn.waiting && turn.endedAt == null" class="sheet__hint">Waiting for the client to be checked in in Mangomint. The timer starts from the check-in.</p>',
    '      <button v-if="turn.endedAt == null" type="button"',
    '        :class="turn.waiting ? \'btn btn--ghost\' : \'btn btn--primary btn--lg\'"',
    '        @click="stopTurn(turn.id)">Stop service</button>',
    '      <button v-else type="button" class="btn btn--ghost" @click="reopenTurn(turn.id)">',
    '        Reopen service</button>',
    '      <template v-if="editingTimes">',
    '        <p class=\"sheet__hint\">Both times are on {{ turn.businessDate }}. Clear the finish to put this service back in progress. The square keeps its place in the column.</p>',
    '        <button type="button" class="btn btn--primary" @click="saveTimes">Save times</button>',
    '        <button type="button" class="btn btn--quiet" @click="editingTimes = false">Cancel time edit</button>',
    '      </template>',
    '      <button v-else type="button" class="btn btn--ghost" @click="editTimes()">Edit times</button>',
    '      <button type="button" class="btn btn--ghost" @click="changing = !changing">',
    '        {{ changing ? "Keep current service" : "Change service" }}</button>',
    '      <button v-if="ui.requestsOn" type="button" class="btn btn--ghost" @click="toggleTurnRequested(turn.id)">',
    '        {{ turn.requested ? "Not a client request" : "Mark as client request" }}</button>',
    '      <button v-if="!confirmRemove" type="button" class="btn btn--danger-ghost"',
    '        @click="confirmRemove = true">Remove turn</button>',
    '      <button v-else type="button" class="btn btn--danger" @click="removeTurn(turn.id)">',
    '        Really remove &mdash; later turns shift up</button>',
    '      <button type="button" class="btn btn--quiet" @click="close">Close</button>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n')
};
